/**
 * ControlHub Aufträge — Frontend-API-Domain.
 *
 * Keine Preise — die Schnittstelle liefert keine Geldbeträge.
 */
import type { RequestFn } from '../http.js';

// ────────────────────────────────────────────────────────────────────────────
// Typen (spiegeln die Schnittstellen-Beschreibung)
// ────────────────────────────────────────────────────────────────────────────

export interface ControlHubOrderItem {
  id: string;
  positionNumber: number;
  kind: 'SERVICE' | 'PRODUCT' | 'FLAT_RATE' | 'TEXT' | 'ALTERNATIVE' | 'OPTIONAL';
  title: string | null;
  description: string | null;
  quantity: number | null;
  unit: string | null;
  isOptional: boolean;
  isAlternative: boolean;
}

export const ORDER_BOARD_STATUSES = [
  'ANGEBOT_ERSTELLT',
  'ANGENOMMEN',
  'BESTELLT',
  'LIEFERBEREIT',
  'TERMIN_VOR_ORT',
  'ERLEDIGT',
] as const;

export type OrderBoardStatus = (typeof ORDER_BOARD_STATUSES)[number];

/** Folgebeleg aus dem ControlHub (ohne Beträge). PDF nur bei Lieferscheinen. */
export interface ControlHubOrderFollowup {
  id: string;
  type: string;
  documentNumber: string | null;
  status: string;
  issuedAt: string;
  pdfAvailable: boolean;
}

export interface ControlHubOrderAttachment {
  id: string;
  titel: string;
  documentId: string;
}

export interface ControlHubOrderStatusEvent {
  id: string;
  fromStatus: OrderBoardStatus | null;
  toStatus: OrderBoardStatus;
  source: 'MANUELL' | 'AUTOMATIK';
  userName: string;
  /** Vermerk der Automatik, z. B. Altbestand */
  note: string | null;
  createdAt: string;
}

export interface ControlHubOrder {
  id: string;
  documentNumber: string;
  status: 'SENT' | 'ACCEPTED' | 'CONVERTED' | 'CANCELLED' | 'REJECTED' | 'EXPIRED';
  acceptedAt: string | null;
  acceptedByName: string | null;
  acceptedComment: string | null;
  customerName: string | null;
  customerNumber: string | null;
  contactName: string | null;
  contactEmail: string | null;
  contactPhone: string | null;
  location: { street: string | null; zip: string | null; city: string } | null;
  introductionText: string | null;
  /** Immer false — im ServiceHub gibt es kein Angebots-PDF. */
  pdfAvailable: boolean;
  itemsTruncated: boolean;
  customerId: string | null;
  sentAt: string | null;
  validUntil: string | null;
  followups: ControlHubOrderFollowup[];
  attachments: ControlHubOrderAttachment[];
  boardStatus: OrderBoardStatus | null;
  boardStatusChangedAt: string | null;
  boardStatusChangedByName: string | null;
  boardStatusSource: 'MANUELL' | 'AUTOMATIK' | null;
  updatedAt: string;
}

export interface ControlHubOrderDetail extends ControlHubOrder {
  items: ControlHubOrderItem[];
  history: ControlHubOrderStatusEvent[];
}

export interface ControlHubBoardOrder extends ControlHubOrder {
  itemCount: number;
}

export interface ControlHubOrderBoardColumn {
  status: OrderBoardStatus;
  label: string;
  orders: ControlHubBoardOrder[];
}

export interface ControlHubOrderBoardResponse {
  columns: ControlHubOrderBoardColumn[];
  generatedAt: string;
}

export interface ControlHubOrdersListResponse {
  orders: ControlHubOrder[];
  total: number;
  page: number;
  pageSize: number;
}

export interface ControlHubCustomerOrdersResponse {
  orders: ControlHubOrder[];
}

export interface ControlHubTokenStatus {
  active: boolean;
  createdAt: string | null;
  lastUsedAt: string | null;
}

export interface ControlHubOrdersStatus {
  tokenStatus: ControlHubTokenStatus;
  hasToken: boolean;
  syncState: {
    lastRunAt: string | null;
    lastError: string | null;
    lastCursor: { updatedAt: string; id: string } | null;
  };
}

export interface ControlHubOrdersSyncStats {
  created: number;
  updated: number;
  skipped: number;
  errors: number;
  /** Nach 3 Fehlläufen übersprungen (Warnung). */
  quarantined?: number;
  pages: number;
  moreRemaining: boolean;
  /** Ein anderer Lauf war schon dran — dieser hat nichts getan. */
  alreadyRunning?: boolean;
}

export interface ControlHubOrdersFilter {
  q?: string;
  status?: 'SENT' | 'ACCEPTED' | 'CONVERTED' | 'CANCELLED';
  customerId?: string;
  page?: number;
  pageSize?: number;
}

// ────────────────────────────────────────────────────────────────────────────
// API-Factory
// ────────────────────────────────────────────────────────────────────────────

export function createControlHubOrdersApi(request: RequestFn) {
  return {
    /** Sync-Status (Token, Cursor, letzter Lauf) */
    async getStatus() {
      return request<ControlHubOrdersStatus>('/admin/controlhub-orders/status');
    },

    /** Sync manuell anstoßen */
    async runSync() {
      return request<ControlHubOrdersSyncStats>('/admin/controlhub-orders/sync', { method: 'POST' });
    },

    /** Token-Status */
    async getTokenStatus() {
      return request<ControlHubTokenStatus>('/admin/controlhub-orders/api-token');
    },

    /** Im ControlHub erzeugten Schlüssel hinterlegen */
    async setToken(token: string) {
      return request<ControlHubTokenStatus>('/admin/controlhub-orders/api-token', {
        method: 'PUT',
        body: JSON.stringify({ token }),
      });
    },

    /** Token widerrufen */
    async revokeToken() {
      return request<ControlHubTokenStatus>('/admin/controlhub-orders/api-token', { method: 'DELETE' });
    },

    /** Basis-URL setzen */
    async setConfig(config: { baseUrl?: string }) {
      return request<{ baseUrl: string }>('/admin/controlhub-orders/config', {
        method: 'PUT',
        body: JSON.stringify(config),
      });
    },

    /** Liste mit Filter */
    async list(filter: ControlHubOrdersFilter = {}) {
      const params = new URLSearchParams();
      if (filter.q) params.set('q', filter.q);
      if (filter.status) params.set('status', filter.status);
      if (filter.customerId) params.set('customerId', filter.customerId);
      if (filter.page) params.set('page', String(filter.page));
      if (filter.pageSize) params.set('pageSize', String(filter.pageSize));
      const qs = params.toString();
      return request<ControlHubOrdersListResponse>(`/admin/controlhub-orders${qs ? `?${qs}` : ''}`);
    },

    /** Detail (mit Positionen) */
    async detail(id: string) {
      return request<ControlHubOrderDetail>(`/admin/controlhub-orders/${encodeURIComponent(id)}`);
    },

    /** Aufträge eines Kunden (für Kundenakte) */
    async forCustomer(customerId: string) {
      return request<ControlHubCustomerOrdersResponse>(
        `/admin/controlhub-orders/customer/${encodeURIComponent(customerId)}`
      );
    },

    /** Auftrags-Board (Spalten; offen + Erledigt der letzten 60 Tage) */
    async orderBoard(q?: string) {
      const qs = q ? `?${new URLSearchParams({ q }).toString()}` : '';
      return request<ControlHubOrderBoardResponse>(`/admin/controlhub-orders/board${qs}`);
    },

    /** Board-Spalte eines Auftrags setzen (wird im Verlauf protokolliert) */
    async setOrderBoardStatus(id: string, status: OrderBoardStatus) {
      return request<{ order: ControlHubBoardOrder; event: ControlHubOrderStatusEvent | null }>(
        `/admin/controlhub-orders/${encodeURIComponent(id)}/status`,
        { method: 'PATCH', body: JSON.stringify({ status }) }
      );
    },

    /** Lieferschein-PDF (Original, preisfrei) */
    lieferscheinPdfUrl(orderId: string, documentId: string) {
      return `/api/admin/controlhub-orders/${encodeURIComponent(orderId)}/documents/${encodeURIComponent(documentId)}/pdf.pdf`;
    },

    /** Packliste (Anhang) */
    packlistePdfUrl(orderId: string, artifactId: string) {
      return `/api/admin/controlhub-orders/${encodeURIComponent(orderId)}/attachments/${encodeURIComponent(artifactId)}/pdf.pdf`;
    },
  };
}
